/**
 * Free gift sync: add or remove the free gift line based on cart subtotal vs threshold.
 * Listens for cart updates; when enabled, ensures the free gift is in cart when
 * subtotal (excluding the gift) >= threshold, and removed when below.
 * Line item property _free_gift (value = variant id) identifies the gift for removal.
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const CONFIG_SELECTOR = '#cart-free-gift-config';
const SYNCING_ATTR = 'data-free-gift-syncing';

function getDrawer() {
  return document.querySelector('cart-drawer-component.cart-drawer') || document.querySelector('.cart-drawer');
}

function setDrawerSyncing(syncing) {
  const drawer = getDrawer();
  if (drawer) {
    if (syncing) drawer.setAttribute(SYNCING_ATTR, 'true');
    else drawer.removeAttribute(SYNCING_ATTR);
  }
}

/** Cart API URL (relative) – from theme routes so we never hit wrong host e.g. https://cart.js/ */
function getCartJsUrl() {
  const theme = typeof Theme !== 'undefined' ? Theme : window.Theme;
  const cartUrl = theme?.routes?.cart_url || '/cart';
  return cartUrl.replace(/\/cart\/?$/, '') + '/cart.js';
}

function getCartAddUrl() {
  const theme = typeof Theme !== 'undefined' ? Theme : window.Theme;
  return theme?.routes?.cart_add_url || getCartJsUrl().replace('.js', '/add.js');
}

function getCartChangeUrl() {
  const theme = typeof Theme !== 'undefined' ? Theme : window.Theme;
  return theme?.routes?.cart_change_url || getCartJsUrl().replace('.js', '/change');
}

/**
 * @returns {{ enabled: boolean, threshold: number, variantId: number, sectionId: string } | null }
 */
function getConfig() {
  const el = document.querySelector(CONFIG_SELECTOR);
  if (!el || !el.textContent) return null;
  try {
    const config = JSON.parse(el.textContent.trim());
    if (!config.enabled || !config.threshold || !config.variantId || !config.sectionId) return null;
    return {
      enabled: true,
      threshold: Number(config.threshold),
      variantId: Number(config.variantId),
      sectionId: String(config.sectionId),
    };
  } catch {
    return null;
  }
}

/**
 * @param {Object} cart - /cart.js response
 * @returns {{ subtotalExcludingGift: number, freeGiftLineIndex: number | null }}
 */
function getFreeGiftState(cart) {
  let subtotalExcludingGift = 0;
  let freeGiftLineIndex = null;
  const items = cart.items || [];
  items.forEach((item, index) => {
    const props = item.properties || {};
    if (props._free_gift != null && String(props._free_gift).trim() !== '') {
      freeGiftLineIndex = index;
    } else {
      subtotalExcludingGift += item.final_line_price || 0;
    }
  });
  return { subtotalExcludingGift, freeGiftLineIndex };
}

function syncFreeGift() {
  const config = getConfig();
  if (!config) return;

  if (document.querySelector('cart-items-component[data-drawer]') == null) return;

  fetch(getCartJsUrl())
    .then((r) => r.json())
    .then((cart) => {
      const { subtotalExcludingGift, freeGiftLineIndex } = getFreeGiftState(cart);
      const shouldHaveGift = subtotalExcludingGift >= config.threshold;
      const hasGift = freeGiftLineIndex !== null;

      if (shouldHaveGift && !hasGift) {
        addFreeGift(config);
      } else if (!shouldHaveGift && hasGift && freeGiftLineIndex !== null) {
        removeFreeGift(config, freeGiftLineIndex + 1);
      }
    })
    .catch((err) => console.error('[cart-free-gift]', err));
}

/**
 * Refreshes the cart drawer section and dispatches cart update (e.g. after add when sections not in response).
 * @param {{ sectionId: string }} config
 * @param {Object} [cartData] - Optional cart/add or cart data for the event
 */
function refreshDrawerAndNotify(config, cartData = {}) {
  sectionRenderer
    .renderSection(config.sectionId, { cache: false, mode: 'hydration' })
    .then(() => {
      const itemCount = cartData.item_count ?? (cartData.items?.length ?? 0);
      document.dispatchEvent(
        new CartUpdateEvent(cartData, config.sectionId, {
          source: 'cart-free-gift',
          itemCount,
          sections: cartData.sections,
        })
      );
    })
    .catch((err) => console.error('[cart-free-gift] refresh', err))
    .finally(() => setDrawerSyncing(false));
}

/**
 * @param {{ variantId: number, sectionId: string }} config
 */
function addFreeGift(config) {
  const form = new FormData();
  form.append('id', config.variantId);
  form.append('quantity', '1');
  form.append('properties[_free_gift]', String(config.variantId));
  form.append('sections', config.sectionId);
  form.append('sections_url', window.location.pathname);

  const url = getCartAddUrl();
  setDrawerSyncing(true);

  fetch(url, {
    ...fetchConfig('javascript', { body: form }),
    headers: { Accept: 'application/json' },
  })
    .then((r) => r.json())
    .then((data) => {
      if (data.status) return;
      const sections = data.sections || {};
      const html = sections[config.sectionId];
      const itemCount = data.item_count ?? (data.items?.length ?? 0);
      if (html) {
        morphSection(config.sectionId, html, 'hydration');
        document.dispatchEvent(
          new CartUpdateEvent(data, config.sectionId, {
            source: 'cart-free-gift',
            itemCount,
            sections,
          })
        );
      } else {
        refreshDrawerAndNotify(config, { ...data, item_count: itemCount });
      }
    })
    .catch((err) => console.error('[cart-free-gift] add', err))
    .finally(() => setDrawerSyncing(false));
}

/**
 * @param {{ sectionId: string }} config
 * @param {number} line - 1-based line index
 */
function removeFreeGift(config, line) {
  const body = JSON.stringify({
    line,
    quantity: 0,
    sections: config.sectionId,
    sections_url: window.location.pathname,
  });

  setDrawerSyncing(true);

  fetch(getCartChangeUrl(), fetchConfig('json', { body }))
    .then((r) => r.text())
    .then((text) => {
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        return;
      }
      if (data.errors) return;
      const sections = data.sections || {};
      const html = sections[config.sectionId];
      if (html) {
        morphSection(config.sectionId, html, 'hydration');
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const countEl = doc.querySelector('[ref="cartItemCount"]');
        const itemCount = countEl ? parseInt(countEl.textContent || '0', 10) : 0;
        document.dispatchEvent(
          new CartUpdateEvent(data, config.sectionId, {
            source: 'cart-free-gift',
            itemCount,
            sections,
          })
        );
      }
    })
    .catch((err) => console.error('[cart-free-gift] remove', err))
    .finally(() => setDrawerSyncing(false));
}

function handleCartUpdate(event) {
  if (event?.detail?.data?.source === 'cart-free-gift') return;
  debouncedSync();
}

const debouncedSync = debounce(syncFreeGift, 400);

const eventName = ThemeEvents?.cartUpdate ?? 'cart:update';
document.addEventListener(eventName, handleCartUpdate);

// Run once on load so drawer state is correct when opened
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setTimeout(syncFreeGift, 300));
} else {
  setTimeout(syncFreeGift, 300);
}
